import { collection, doc, setDoc, onSnapshot, deleteDoc, query, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Firebase WebRTC Signaling Service
 * Matches iOS implementation for cross-platform compatibility
 *
 * Firestore Structure:
 * - Offers: meetings/{meetingId}/signaling/{toParticipantId}/offers/{fromParticipantId}
 * - Answers: meetings/{meetingId}/signaling/{toParticipantId}/answers/{fromParticipantId}
 * - ICE Candidates: meetings/{meetingId}/signaling/{toParticipantId}/candidates/{fromParticipantId}_{candidateId}
 */

class FirebaseWebRTCSignaling {
  constructor(meetingId, localParticipantId, onRemoteStream, onConnectionStateChange) {
    this.meetingId = meetingId;
    this.localParticipantId = localParticipantId;
    this.onRemoteStream = onRemoteStream;
    this.onConnectionStateChange = onConnectionStateChange;

    // Store peer connections: Map<remoteParticipantId, RTCPeerConnection>
    this.peerConnections = new Map();

    // Store unsubscribe functions for cleanup
    this.unsubscribers = [];

    // ICE servers configuration
    this.iceServers = [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
      { urls: 'stun:stun2.l.google.com:19302' }
    ];

    // Local stream reference
    this.localStream = null;
  }

  /**
   * Set local media stream
   */
  setLocalStream(stream) {
    this.localStream = stream;
    console.log('🎥 Local stream set for WebRTC signaling');
  }

  /**
   * Start listening for incoming offers, answers, and ICE candidates
   */
  startListening() {
    console.log('👂 Starting Firebase signaling listeners for participant:', this.localParticipantId);

    this.listenForOffers();
    this.listenForAnswers();
    this.listenForCandidates();
  }

  /**
   * Listen for incoming offers
   */
  listenForOffers() {
    const offersPath = `meetings/${this.meetingId}/signaling/${this.localParticipantId}/offers`;
    const offersRef = collection(db, offersPath);

    const unsubscribe = onSnapshot(offersRef, async (snapshot) => {
      for (const change of snapshot.docChanges()) {
        if (change.type === 'added') {
          const fromParticipantId = change.doc.id;
          const offerData = change.doc.data();

          console.log('📨 Received offer from:', fromParticipantId);

          await this.handleIncomingOffer(fromParticipantId, offerData);

          // Auto-cleanup: Delete offer after processing (match iOS behavior)
          try {
            await deleteDoc(change.doc.ref);
          } catch (error) {
            // Silently handle cleanup errors - connection works regardless
            console.debug('Offer cleanup skipped:', error.code);
          }
        }
      }
    });

    this.unsubscribers.push(unsubscribe);
  }

  /**
   * Listen for incoming answers
   */
  listenForAnswers() {
    const answersPath = `meetings/${this.meetingId}/signaling/${this.localParticipantId}/answers`;
    const answersRef = collection(db, answersPath);

    const unsubscribe = onSnapshot(answersRef, async (snapshot) => {
      for (const change of snapshot.docChanges()) {
        if (change.type === 'added') {
          const fromParticipantId = change.doc.id;
          const answerData = change.doc.data();

          console.log('📨 Received answer from:', fromParticipantId);

          await this.handleIncomingAnswer(fromParticipantId, answerData);

          // Auto-cleanup: Delete answer after processing
          try {
            await deleteDoc(change.doc.ref);
          } catch (error) {
            // Silently handle cleanup errors - connection works regardless
            console.debug('Answer cleanup skipped:', error.code);
          }
        }
      }
    });

    this.unsubscribers.push(unsubscribe);
  }

  /**
   * Listen for incoming ICE candidates
   */
  listenForCandidates() {
    const candidatesPath = `meetings/${this.meetingId}/signaling/${this.localParticipantId}/candidates`;
    const candidatesRef = collection(db, candidatesPath);

    const unsubscribe = onSnapshot(candidatesRef, async (snapshot) => {
      for (const change of snapshot.docChanges()) {
        if (change.type === 'added') {
          const docId = change.doc.id;
          const candidateData = change.doc.data();

          // Parse fromParticipantId from composite ID: {fromParticipantId}_{candidateId}
          const fromParticipantId = docId.split('_')[0];

          console.log('🧊 Received ICE candidate from:', fromParticipantId);

          await this.handleIncomingCandidate(fromParticipantId, candidateData);

          // Auto-cleanup: Delete candidate after processing
          try {
            await deleteDoc(change.doc.ref);
          } catch (error) {
            // Silently handle cleanup errors - connection works regardless
            console.debug('Candidate cleanup skipped:', error.code);
          }
        }
      }
    });

    this.unsubscribers.push(unsubscribe);
  }

  /**
   * Create peer connection for a remote participant
   */
  createPeerConnection(remoteParticipantId) {
    if (this.peerConnections.has(remoteParticipantId)) {
      console.log('♻️  Peer connection already exists for:', remoteParticipantId);
      return this.peerConnections.get(remoteParticipantId);
    }

    console.log('🔗 Creating peer connection for:', remoteParticipantId);

    const pc = new RTCPeerConnection({ iceServers: this.iceServers });

    // Add local stream tracks to peer connection
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => {
        pc.addTrack(track, this.localStream);
        console.log('➕ Added local track:', track.kind);
      });
    }

    // Handle remote stream
    pc.ontrack = (event) => {
      console.log('📺 Received remote track from:', remoteParticipantId, event.track.kind);
      if (this.onRemoteStream) {
        this.onRemoteStream(remoteParticipantId, event.streams[0]);
      }
    };

    // Handle ICE candidates
    pc.onicecandidate = async (event) => {
      if (event.candidate) {
        console.log('🧊 Sending ICE candidate to:', remoteParticipantId);
        await this.sendIceCandidate(remoteParticipantId, event.candidate);
      }
    };

    // Handle connection state changes
    pc.onconnectionstatechange = () => {
      console.log('🔌 Connection state with', remoteParticipantId, ':', pc.connectionState);
      if (this.onConnectionStateChange) {
        this.onConnectionStateChange(remoteParticipantId, pc.connectionState);
      }

      // Cleanup on disconnect
      if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed' || pc.connectionState === 'closed') {
        this.closePeerConnection(remoteParticipantId);
      }
    };

    this.peerConnections.set(remoteParticipantId, pc);
    return pc;
  }

  /**
   * Create and send offer to remote participant
   */
  async createOffer(remoteParticipantId) {
    console.log('📤 Creating offer for:', remoteParticipantId);

    // CRITICAL: Check if local stream exists before creating offer
    if (!this.localStream) {
      console.error('❌ Cannot create offer - local stream not set!');
      console.log('   Call setLocalStream() before creating offers');
      return;
    }

    console.log('✅ Local stream available with tracks:', this.localStream.getTracks().map(t => t.kind));

    const pc = this.createPeerConnection(remoteParticipantId);

    try {
      const offer = await pc.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true
      });

      await pc.setLocalDescription(offer);

      // Send offer via Firebase (match iOS structure)
      const offerPath = `meetings/${this.meetingId}/signaling/${remoteParticipantId}/offers/${this.localParticipantId}`;
      console.log('📝 Writing offer to Firestore:', offerPath);
      await setDoc(doc(db, offerPath), {
        type: offer.type,
        sdp: offer.sdp,
        timestamp: Math.floor(Date.now() / 1000) // Unix timestamp in seconds (match iOS)
      });

      console.log('✅ Offer sent to:', remoteParticipantId);
    } catch (error) {
      console.error('❌ Error creating offer:', error);
    }
  }

  /**
   * Handle incoming offer
   */
  async handleIncomingOffer(fromParticipantId, offerData) {
    console.log('📥 Processing offer from:', fromParticipantId);

    // Check if local stream exists before answering
    if (!this.localStream) {
      console.error('❌ Cannot answer offer - local stream not set!');
      return;
    }

    console.log('✅ Local stream available for answer');

    const pc = this.createPeerConnection(fromParticipantId);

    try {
      console.log('📝 Setting remote description from offer');
      await pc.setRemoteDescription(new RTCSessionDescription({
        type: offerData.type,
        sdp: offerData.sdp
      }));

      console.log('📝 Creating answer');
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      // Send answer via Firebase
      const answerPath = `meetings/${this.meetingId}/signaling/${fromParticipantId}/answers/${this.localParticipantId}`;
      console.log('📝 Writing answer to Firestore:', answerPath);
      await setDoc(doc(db, answerPath), {
        type: answer.type,
        sdp: answer.sdp,
        timestamp: Math.floor(Date.now() / 1000) // Unix timestamp in seconds
      });

      console.log('✅ Answer sent to:', fromParticipantId);
    } catch (error) {
      console.error('❌ Error handling offer:', error);
    }
  }

  /**
   * Handle incoming answer
   */
  async handleIncomingAnswer(fromParticipantId, answerData) {
    const pc = this.peerConnections.get(fromParticipantId);

    if (!pc) {
      console.error('❌ No peer connection found for:', fromParticipantId);
      return;
    }

    try {
      await pc.setRemoteDescription(new RTCSessionDescription({
        type: answerData.type,
        sdp: answerData.sdp
      }));

      console.log('✅ Answer processed from:', fromParticipantId);
    } catch (error) {
      console.error('❌ Error handling answer:', error);
    }
  }

  /**
   * Send ICE candidate
   */
  async sendIceCandidate(remoteParticipantId, candidate) {
    const candidateId = `candidate_${Date.now()}`;
    const candidatePath = `meetings/${this.meetingId}/signaling/${remoteParticipantId}/candidates/${this.localParticipantId}_${candidateId}`;

    await setDoc(doc(db, candidatePath), {
      id: candidateId,
      candidate: candidate.candidate,
      sdpMLineIndex: candidate.sdpMLineIndex,
      sdpMid: candidate.sdpMid || null,
      timestamp: Math.floor(Date.now() / 1000) // Unix timestamp in seconds
    });
  }

  /**
   * Handle incoming ICE candidate
   */
  async handleIncomingCandidate(fromParticipantId, candidateData) {
    const pc = this.peerConnections.get(fromParticipantId);

    if (!pc) {
      console.warn('⚠️  No peer connection found for candidate from:', fromParticipantId);
      return;
    }

    try {
      await pc.addIceCandidate(new RTCIceCandidate({
        candidate: candidateData.candidate,
        sdpMLineIndex: candidateData.sdpMLineIndex,
        sdpMid: candidateData.sdpMid
      }));

      console.log('✅ ICE candidate added from:', fromParticipantId);
    } catch (error) {
      console.error('❌ Error adding ICE candidate:', error);
    }
  }

  /**
   * Close peer connection
   */
  closePeerConnection(remoteParticipantId) {
    const pc = this.peerConnections.get(remoteParticipantId);

    if (pc) {
      console.log('🔌 Closing peer connection for:', remoteParticipantId);
      pc.close();
      this.peerConnections.delete(remoteParticipantId);
    }
  }

  /**
   * Cleanup all connections and listeners
   */
  cleanup() {
    console.log('🧹 Cleaning up Firebase WebRTC signaling');

    // Close all peer connections
    this.peerConnections.forEach((pc, participantId) => {
      this.closePeerConnection(participantId);
    });

    // Unsubscribe from all listeners
    this.unsubscribers.forEach(unsubscribe => unsubscribe());
    this.unsubscribers = [];
  }
}

export default FirebaseWebRTCSignaling;
